import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Features component for SignNow brand feature showcase
 *
 * @product signnow
 * @version 1.0.2
 * @category SignNow
 * @contentConfig Features component for showcasing key product capabilities
 * @example <sn-features-type-11 constants='{"FEATURES_CARD_1_HEADING":"Connect airSlate SignNow to your apps","FEATURES_CARD_1_DESCRIPTION":"Add airSlate SignNow\'s functionality to the apps you use and love. Increase your organization\'s productivity, reduce document errors, and delight your employees and customers.","FEATURES_CARD_1_ACTION":"Check out airSlate SignNow integrations","LINK__FEATURES_CARD_1_ACTION":"/integrations","FEATURES_INTEGRATION_GDRIVE":"signNow for Google Drive","LINK__FEATURES_INTEGRATION_GDRIVE":"/integrations/googledrive","FEATURES_INTEGRATION_TEAMS":"signNow for Microsoft Teams","LINK__FEATURES_INTEGRATION_TEAMS":"/integrations/teams","FEATURES_INTEGRATION_WORD":"signNow for Word","LINK__FEATURES_INTEGRATION_WORD":"/integrations/word","FEATURES_INTEGRATION_SALESFORCE":"signNow for Salesforce","LINK__FEATURES_INTEGRATION_SALESFORCE":"/integrations/salesforce","FEATURES_INTEGRATION_NETSUITE":"signNow for Netsuite","LINK__FEATURES_INTEGRATION_NETSUITE":"/integrations/netsuite","FEATURES_CARD_2_HEADING":"Data accuracy, security, and compliance","FEATURES_CARD_2_DESCRIPTION":"airSlate SignNow is committed to protecting your sensitive information by complying with global industry-specific.","FEATURES_CARD_2_ACTION":"Learn more about security","LINK__FEATURES_CARD_2_ACTION":"/security"}'></sn-features-type-11>
 * @constant FEATURES_CARD_1_HEADING - "Connect airSlate SignNow to your apps" - First card heading
 * @constant FEATURES_CARD_1_DESCRIPTION - "Add airSlate SignNow's functionality to the apps you use and love. Increase your organization's productivity, reduce document errors, and delight your employees and customers." - First card description
 * @constant FEATURES_CARD_1_ACTION - "Check out airSlate SignNow integrations" - First card action text
 * @constant LINK__FEATURES_CARD_1_ACTION - "/integrations" - Link for "Check out airSlate SignNow integrations" button
 * @constant FEATURES_INTEGRATION_GDRIVE - "signNow for Google Drive" - Google Drive integration label
 * @constant LINK__FEATURES_INTEGRATION_GDRIVE - "/integrations/googledrive" - Link for the "signNow for Google Drive" integration
 * @constant FEATURES_INTEGRATION_TEAMS - "signNow for Microsoft Teams" - Microsoft Teams integration label
 * @constant LINK__FEATURES_INTEGRATION_TEAMS - "/integrations/teams" - Link for the "signNow for Microsoft Teams" integration
 * @constant FEATURES_INTEGRATION_WORD - "signNow for Word" - Microsoft Word integration label
 * @constant LINK__FEATURES_INTEGRATION_WORD - "/integrations/word" - Link for the "signNow for Word" integration
 * @constant FEATURES_INTEGRATION_SALESFORCE - "signNow for Salesforce" - Salesforce integration label
 * @constant LINK__FEATURES_INTEGRATION_SALESFORCE - "/integrations/salesforce" - Link for the "signNow for Salesforce" integration
 * @constant FEATURES_INTEGRATION_NETSUITE - "signNow for Netsuite" - NetSuite integration label
 * @constant LINK__FEATURES_INTEGRATION_NETSUITE - "/integrations/netsuite" - Link for the "signNow for Netsuite" integration
 * @constant FEATURES_CARD_2_HEADING - "Data accuracy, security, and compliance" - Second card heading
 * @constant FEATURES_CARD_2_DESCRIPTION - "airSlate SignNow is committed to protecting your sensitive information by complying with global industry-specific." - Second card description
 * @constant FEATURES_CARD_2_ACTION - "Learn more about security" - Second card action text
 * @constant LINK__FEATURES_CARD_2_ACTION - "/security" - Link for "Learn more about security" button
 * @slot cards - {required} - Feature cards for displaying different capabilities
 */
@Component({
  tag: 'sn-features-type-11',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFeaturesType11 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  render() {
    return (
      <features-type-11>
        <features-card-type-4 slot="cards" variant="dark" class="features-card-type-4--security">
          <me-heading slot="heading" variant="3">
            {getConstant(this.constants, 'FEATURES_CARD_2_HEADING', 'Data accuracy, security, and compliance')}
          </me-heading>
          <me-text slot="description">
            {getConstant(
              this.constants,
              'FEATURES_CARD_2_DESCRIPTION',
              'airSlate SignNow is committed to protecting your sensitive information by complying with global industry-specific.',
            )}
          </me-text>
          <me-button
            slot="actions"
            variant="ghost"
            icon="arrows--right-arrow-alt"
            icon-position="after"
            href={getConstant(this.constants, 'LINK__FEATURES_CARD_2_ACTION', '/security')}
            tag="a"
          >
            {getConstant(this.constants, 'FEATURES_CARD_2_ACTION', 'Learn more about security')}
          </me-button>
          <me-picture slot="media" src="https://marketing-static.signnow.com/330/images/e-signature/security2x.png" aspectRatio="1.95 / 1"></me-picture>
        </features-card-type-4>
        <features-card-type-4 slot="cards" variant="default" class="features-card-type-4--primary">
          <me-heading slot="heading" variant="3">
            {getConstant(this.constants, 'FEATURES_CARD_1_HEADING', 'Connect airSlate SignNow to your apps')}
          </me-heading>
          {/* <me-text slot="description">
            {getConstant(
              this.constants,
              'FEATURES_CARD_1_DESCRIPTION',
              "Add airSlate SignNow's functionality to the apps you use and love. Increase your organization's productivity, reduce document errors, and delight your employees and customers.",
            )}
          </me-text> */}
          <me-button
            slot="actions"
            variant="ghost"
            icon="arrows--right-arrow-alt"
            icon-position="after"
            href={getConstant(this.constants, 'LINK__FEATURES_CARD_1_ACTION', '/integrations')}
            tag="a"
          >
            {getConstant(this.constants, 'FEATURES_CARD_1_ACTION', 'Check out airSlate SignNow integrations')}
          </me-button>
          <div slot="media" class="apps">
            <div class="apps__item">
              <div class="apps__wrapper">
                <div class="apps__logo">
                  <me-picture
                    srcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/g-drive.png 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/g-drive@2x.png 2x"
                    webpSrcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/g-drive.webp 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/g-drive@2x.webp 2x"
                    aspectRatio="1/1"
                    class="apps__icon apps__icon--gdrive"
                    alt={getConstant(this.constants, 'FEATURES_INTEGRATION_GDRIVE', 'signNow for Google Drive')}
                  />
                </div>
                <a
                  class="apps__link"
                  href={getConstant(this.constants, 'LINK__FEATURES_INTEGRATION_GDRIVE', '/integrations/googledrive')}
                  aria-label={getConstant(this.constants, 'FEATURES_INTEGRATION_GDRIVE', 'signNow for Google Drive')}
                ></a>
              </div>
            </div>
            <div class="apps__item">
              <div class="apps__wrapper">
                <div class="apps__logo">
                  <me-picture
                    srcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/ms-teams.png 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/ms-teams@2x.png 2x"
                    webpSrcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/ms-teams.webp 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/ms-teams@2x.webp 2x"
                    aspectRatio="1/1"
                    class="apps__icon apps__icon--ms-teams"
                    alt={getConstant(this.constants, 'FEATURES_INTEGRATION_TEAMS', 'signNow for Microsoft Teams')}
                  />
                </div>
                <a
                  class="apps__link"
                  href={getConstant(this.constants, 'LINK__FEATURES_INTEGRATION_TEAMS', '/integrations/teams')}
                  aria-label={getConstant(this.constants, 'FEATURES_INTEGRATION_TEAMS', 'signNow for Microsoft Teams')}
                ></a>
              </div>
            </div>
            <div class="apps__item">
              <div class="apps__wrapper">
                <div class="apps__logo">
                  <me-picture
                    srcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/ms-word.png 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/ms-word@2x.png 2x"
                    webpSrcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/ms-word.webp 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/ms-word@2x.webp 2x"
                    aspectRatio="1/1"
                    class="apps__icon apps__icon--ms-word"
                    alt={getConstant(this.constants, 'FEATURES_INTEGRATION_WORD', 'signNow for Word')}
                  />
                </div>
                <a
                  class="apps__link"
                  href={getConstant(this.constants, 'LINK__FEATURES_INTEGRATION_WORD', '/integrations/word')}
                  aria-label={getConstant(this.constants, 'FEATURES_INTEGRATION_WORD', 'signNow for Word')}
                ></a>
              </div>
            </div>
            <div class="apps__item">
              <div class="apps__wrapper">
                <div class="apps__logo">
                  <img
                    loading="lazy"
                    class="apps__icon apps__icon--salesforce"
                    src="https://marketing-static.signnow.com/340/images/solutions-catalog/salesforce.svg"
                    alt={getConstant(this.constants, 'FEATURES_INTEGRATION_SALESFORCE', 'signNow for Salesforce')}
                  />
                </div>
                <a
                  class="apps__link"
                  href={getConstant(this.constants, 'LINK__FEATURES_INTEGRATION_SALESFORCE', '/integrations/salesforce')}
                  aria-label={getConstant(this.constants, 'FEATURES_INTEGRATION_SALESFORCE', 'signNow for Salesforce')}
                ></a>
              </div>
            </div>
            <div class="apps__item apps__item--double-size">
              <div class="apps__wrapper">
                <div class="apps__logo">
                  <me-picture
                    srcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/netsuite.png 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/netsuite@2x.png 2x"
                    webpSrcSet="https://marketing-static.signnow.com/340/images/solutions-catalog/netsuite.webp 1x, https://marketing-static.signnow.com/340/images/solutions-catalog/netsuite@2x.webp 2x"
                    aspectRatio="1/1"
                    class="apps__icon apps__icon--netsuite"
                    alt={getConstant(this.constants, 'FEATURES_INTEGRATION_NETSUITE', 'signNow for Netsuite')}
                  />
                </div>
                <a
                  class="apps__link"
                  href={getConstant(this.constants, 'LINK__FEATURES_INTEGRATION_NETSUITE', '/integrations/netsuite')}
                  aria-label={getConstant(this.constants, 'FEATURES_INTEGRATION_NETSUITE', 'signNow for Netsuite')}
                ></a>
              </div>
            </div>
          </div>
        </features-card-type-4>
      </features-type-11>
    );
  }
}
